/**
 * Handles the Facebook (Meta) OAuth redirect after login in the SPA.
 *
 * On success, Facebook returns tokens in the URL hash fragment. On cancel/deny,
 * error fields appear on the query string. This module normalizes both, stores
 * the result in sessionStorage, and redirects back to the Meta integration page.
 */

const STORAGE_KEY = 'fb_token'

type FacebookLoginType = 'new' | 'refresh'

interface DecodedFacebookState {
  accountId: string | null
  loginType: FacebookLoginType
}

/**
 * Parses the OAuth `state` we send with the auth request.
 *
 * When we append a suffix: trailing `n` = new connection, `r` = refresh; the
 * leading portion is the account id. Otherwise the whole string is treated as
 * the account id with login type `new`.
 */
function decodeFacebookState(state: string | null): DecodedFacebookState {
  if (!state) {
    return { accountId: null, loginType: 'new' }
  }

  const lastChar = state.slice(-1)
  const isRefresh = lastChar === 'r'
  const isNew = lastChar === 'n'

  if (isRefresh || isNew) {
    return {
      accountId: state.slice(0, -1),
      loginType: isRefresh ? 'refresh' : 'new',
    }
  }

  return {
    accountId: state,
    loginType: 'new',
  }
}

/**
 * Run on app bootstrap (before mount). If the current URL is an OAuth callback,
 * consumes it: writes `fb_token` / `fb_oauth_result` to sessionStorage and
 * replaces the location to `/accounts/:id/integrations/meta` when an account
 * id is present. No-op if the URL is not a Facebook OAuth response.
 */
export function handleOAuthRedirect(): void {
  // Success: implicit grant returns access_token, expires_in, state in the hash
  if (window.location.hash.includes('access_token') && window.location.hash.includes('state')) {
    const hash = window.location.hash.substring(1)
    const params = new URLSearchParams(hash)
    const expiresIn = params.get('expires_in')
    const accessToken = params.get('access_token')
    const state = params.get('state')

    const { accountId, loginType } = decodeFacebookState(state)

    const token = {
      accessToken,
      expiresAt: Date.now() + parseInt(expiresIn ?? '', 10) * 1000,
      loginType,
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(token))
    sessionStorage.setItem('fb_oauth_result', 'success')

    if (accountId) {
      const target = `/accounts/${accountId}/integrations/meta`
      window.location.replace(target)
      return
    }
  }

  // Cancel / denied: e.g. ?error=access_denied&error_reason=user_denied&state=...&action=cancel
  const search = window.location.search
  if (search && search.length > 1) {
    const queryParams = new URLSearchParams(search)
    const error = queryParams.get('error')
    const errorReason = queryParams.get('error_reason')
    const action = queryParams.get('action')
    const state = queryParams.get('state')

    const isAccessDenied =
      error === 'access_denied' || errorReason === 'user_denied' || action === 'cancel'
    if (isAccessDenied && state) {
      const { accountId } = decodeFacebookState(state)

      sessionStorage.setItem('fb_oauth_result', 'cancel')

      if (accountId) {
        const target = `/accounts/${accountId}/integrations/meta`
        window.location.replace(target)
        return
      }
    }
  }
}
